import React, { useState, useEffect } from "react";
import usePageTitle from '../../hooks/usePageTitle';
import { useParams, useNavigate } from "react-router-dom";
import { SmartProfileReportCard } from "../../components/ui/shared";


// ─── Format Helpers (DESIGN SYSTEM — 2 decimal, K/M untuk >= 1000) ──────────
const fmt2 = v => {
  const n = Number(v)||0;
  if(Math.abs(n)>=1000000) return (n/1000000).toFixed(2)+"M";
  if(Math.abs(n)>=1000) return (n/1000).toFixed(2)+"K";
  return n.toFixed(2);
};
const fmtPct = v => fmt2(Number(v)||0)+"%";
const fmtDollar = v => { const n=Number(v)||0; return (n>=0?"$":"-$")+fmt2(Math.abs(n)); };
const fmtDollarSign = v => { const n=Number(v)||0; return (n>=0?"+$":"-$")+fmt2(Math.abs(n)); };
// ─────────────────────────────────────────────────────────────────────────────


const C = {
  bg:"var(--bg)", card:"var(--bg-surface)", elevated:"var(--bg-elevated)", border:"var(--border)",
  text:"var(--text-main)", muted:"#8c92b0", accent:"#4e9af1",
};
const card = { background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:20, marginBottom:16 };
const cardPad = { background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"20px 24px", marginBottom:16 };
const th = { textAlign:"left", padding:"8px 10px", fontSize:11, color:C.muted, fontWeight:700 };
const td = { padding:"8px 10px", fontSize:12, color:C.text };

function GradeCircle({ grade, score }) {
  const color = grade?.startsWith("A")?"var(--grade-a)":grade?.startsWith("B")?"var(--grade-b)":grade?.startsWith("C")?"var(--grade-c)":"var(--grade-f)";
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
      <div style={{ width:80, height:80, borderRadius:"50%", border:`4px solid ${color}`,
        display:"flex", alignItems:"center", justifyContent:"center",
        fontSize:22, fontWeight:900, color, background:"rgba(255,255,255,0.03)" }}>
        {grade||"?"}
      </div>
      <div style={{ fontSize:20, fontWeight:900, color }}>{fmt2(score)}</div>
      <div style={{ fontSize:11, color:C.muted }}>AlphaScore</div>
    </div>

  );
}

function PillarBar({ name, score, reason }) {
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
        <span style={{ fontSize:12, fontWeight:600, color:C.muted }}>{name}</span>
        <span style={{ fontSize:12, fontWeight:800, color:"var(--text-main)" }}>{score?.toFixed(0)}</span>
      </div>
      <div style={{ height:4, background:"rgba(255,255,255,0.06)", borderRadius:2, overflow:"hidden" }}>
        <div style={{ height:"100%", width:`${Math.min(score,100)}%`,
          background:"var(--accent)", borderRadius:2,
          transition:"width 0.8s ease",
          opacity: 0.4 + (Math.min(score,100)/100)*0.6 }} />
      </div>
      <div style={{ fontSize:10, color:"var(--text-faint)", marginTop:2 }}>{reason}</div>
    </div>
  );
}


const riskColor = (r) => {
  if(r === "VERIFIED_SAFE") return "#a855f7";
  if(r === "LOW")           return "var(--success)";
  if(r === "MEDIUM")        return "var(--warning)";
  if(r === "HIGH")          return "var(--warning)";
  if(r === "EXTREME")       return "var(--grade-f)";
  return "#8c92b0";
};

const riskLabel = (r) => {
  if(r === "VERIFIED_SAFE") return " SAFE";
  if(r === "LOW")           return "▼ LOW";
  if(r === "MEDIUM")        return "● MED";
  if(r === "HIGH")          return "▲ HIGH";
  if(r === "EXTREME")       return "   EXTREME";
  return r || "—";
};

// ─── SMART VIEW — 4 Layer Investor Intelligence ──────────────────────────────

const gradeColor = g => g?.startsWith("A")?"var(--grade-a)":g?.startsWith("B")?"var(--grade-b)":g?.startsWith("C")?"var(--grade-c)":"var(--grade-f)";

const riskLevelMeta = r => {
  const m = {
    VERIFIED_SAFE: { label:"Very Low Risk", emoji:"  ", color:"var(--success)", investor:"Conservative Investors" },
    LOW:           { label:"Low Risk",      emoji:"  ", color:"var(--success)", investor:"Conservative Investors" },
    MEDIUM:        { label:"Med Risk",      emoji:"  ", color:"var(--warning)", investor:"Balanced Investors" },
    HIGH:          { label:"High Risk",     emoji:"", color:"var(--warning)", investor:"Growth-Seeking Investors" },
    EXTREME:       { label:"Extreme Risk",  emoji:"", color:"var(--risk-extreme)", investor:"High-Risk Tolerant Investors" },
  };
  return m[r] || { label:r||"Unknown", emoji:"", color:"var(--text-muted)", investor:"All Investors" };
};

const ddTrendMeta = t => {
  const tip = "Average drawdown last 7 days vs 30-day average. Short-term trend only — does not reflect overall risk level.";
  if(t==="IMPROVING") return { label:"DD ↓ (7d)", color:"var(--success)", sub:"last 7d vs 30d avg", tip };
  if(t==="WORSENING") return { label:"DD ↑ (7d)", color:"var(--danger)",  sub:"last 7d vs 30d avg", tip };
  return { label:"DD → Stable", color:"var(--text-muted)", sub:"last 7d vs 30d avg", tip };
};

const survLabel = s => s>=80?"Score indicates strong historical resilience to drawdowns":s>=60?"Score indicates moderate resilience to market stress":s>=40?"Score indicates limited resilience — historically sensitive to volatility":"Score indicates low historical resilience to drawdown periods";
const scaleLabel = s => s>=80?"Historically performed consistently across larger position sizes":s>=60?"Historically consistent across medium position sizes":s>=40?"Historically observed with smaller position sizes":"Historical data based on limited position size range";
const survMonths = s => s>=80?"Historically high continuity score":s>=60?"Historically moderate continuity score":s>=40?"Historically variable continuity score":"Historically low continuity score";

const severityStyle = sev => {
  if(sev==="CRITICAL") return { color:"var(--flag-critical-text)", bg:"var(--flag-critical-bg)", border:"var(--flag-critical-border)", label:"CRITICAL" };
  if(sev==="MAJOR")    return { color:"var(--warning)", bg:"rgba(245,158,11,0.10)", border:"rgba(245,158,11,0.25)", label:"MAJOR" };
  return { color:"var(--text-muted)", bg:"rgba(255,255,255,0.04)", border:"rgba(255,255,255,0.10)", label:"MINOR" };
};

function DDSparkline({ currentDD, maxDD, ddTrend }) {
  const dd = Number(currentDD||0);
  const max = Number(maxDD||0)||1;
  const pct = Math.min(dd/max,1)*100;
  const color = dd>=40?"var(--danger)":dd>=20?"var(--warning)":"var(--success)";
  const trend = ddTrendMeta(ddTrend);
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
        <span style={{fontSize:22,fontWeight:900,color}}>{dd.toFixed(1)}%</span>
        <span title={trend.tip} style={{fontSize:11,fontWeight:700,color:trend.color,cursor:"help",borderBottom:"1px dotted currentColor"}}>{trend.label}</span>
      </div>
      <div style={{height:6,background:"rgba(255,255,255,0.06)",borderRadius:3,overflow:"hidden",marginBottom:4}}>
        <div style={{height:"100%",width:`${pct}%`,background:color,borderRadius:3,transition:"width 0.6s ease"}}/>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:"var(--text-faint)"}}>
        <span>Current DD</span>
        <span>Max: {max.toFixed(1)}%</span>
      </div>
      <div style={{fontSize:10,color:"var(--text-faint)",marginTop:2}}>{trend.sub}</div>
    </div>
  );
}

function ScoreGauge({ label, score, subLabel }) {
  const s = Number(score||0);
  const color = s>=80?"var(--success)":s>=60?"var(--accent)":s>=40?"var(--warning)":"var(--danger)";
  const pct = Math.min(s,100);
  return (
    <div style={{flex:1,background:"var(--bg-elevated)",border:"1px solid var(--border)",borderRadius:10,padding:"14px 16px"}}>
      <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>{label}</div>
      <div style={{display:"flex",alignItems:"baseline",gap:3,marginBottom:8}}>
        <span style={{fontSize:26,fontWeight:900,color}}>{s.toFixed(0)}</span>
        <span style={{fontSize:11,color:"var(--text-faint)"}}>/100</span>
      </div>
      <div style={{height:4,background:"rgba(255,255,255,0.06)",borderRadius:2,overflow:"hidden",marginBottom:8}}>
        <div style={{height:"100%",width:`${pct}%`,background:color,borderRadius:2}}/>
      </div>
      <div style={{fontSize:11,color:"var(--text-muted)",lineHeight:1.5}}>{subLabel}</div>
    </div>
  );
}

function SmartView({ profile, detail, riskFlags, pillars: pillarsFromAuth, colors:C, cardStyle, smartReport }) {
  const [showAdvanced, setShowAdvanced] = React.useState(false);

  // Merge data — detail dari public endpoint, profile dari auth endpoint
  const grade       = profile?.grade || detail?.grade || "?";
  const alphaScore  = Number(profile?.alpha_score || detail?.alphaScore || 0);
  const riskLevel   = profile?.risk_level || detail?.riskLevel || "MEDIUM";
  const riskProfile = detail?.riskProfile || profile?.risk_profile || "";
  const roi         = Number(profile?.roi || detail?.roi || 0);
  const currentDD   = Number(detail?.currentDD || 0);
  const maxDD       = Number(profile?.max_drawdown_pct || detail?.maxDD || 0);
  const ddTrend     = detail?.ddTrend || "STABLE";
  const survivability = Number(profile?.survivability || detail?.survivability || 0);
  const scalability   = Number(profile?.scalability   || detail?.scalability   || 0);
  const pillars     = Array.isArray(detail?.pillars) && detail.pillars.length > 0 ? detail.pillars : (pillarsFromAuth||[]);
  const flags       = Array.isArray(detail?.flags) && detail.flags.length > 0 ? detail.flags : riskFlags;
  const signalSyncStatus   = detail?.signalSyncStatus || "NONE";
  const signalSyncStrategy = detail?.signalSyncStrategy || "";
  const planROI      = Number(detail?.planROI || 0);
  const planWinRate  = Number(detail?.planWinRate || 0);
  const planMaxDD    = Number(detail?.planMaxDD || 0);
  const planTotalTrades = Number(detail?.planTotalTrades || 0);

  const rlMeta  = riskLevelMeta(riskLevel);
  const gColor  = gradeColor(grade);
  const activeFlags = flags.filter(f => f.severity==="CRITICAL"||f.severity==="MAJOR");
  const minorFlags  = flags.filter(f => f.severity==="MINOR");

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>

      {/* ── LAYER 1 — First Impression ───────────────────────────── */}
      <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:14,padding:"20px 24px"}}>
        <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:14}}>
          AlphaRank™ Overview
        </div>
        <div style={{display:"flex",gap:20,alignItems:"center",flexWrap:"wrap"}}>
          {/* Grade Circle */}
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4,minWidth:80}}>
            <div style={{width:72,height:72,borderRadius:"50%",border:`3px solid ${gColor}`,
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:20,fontWeight:900,color:gColor,background:"rgba(255,255,255,0.02)"}}>
              {grade}
            </div>
            <div style={{fontSize:17,fontWeight:900,color:gColor}}>{alphaScore.toFixed(1)}</div>
            <div style={{fontSize:9,color:"var(--text-faint)",textTransform:"uppercase",letterSpacing:"0.06em"}}>AlphaScore</div>
          </div>

          {/* Divider */}
          <div style={{width:1,height:72,background:"var(--border)",flexShrink:0}}/>

          {/* Risk badge */}
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Risk Level</div>
            <div style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 12px",borderRadius:20,
              background:`${rlMeta.color}18`,border:`1px solid ${rlMeta.color}44`}}>
              <span style={{fontSize:13}}>{rlMeta.emoji}</span>
              <span style={{fontSize:13,fontWeight:800,color:rlMeta.color}}>{rlMeta.label}</span>
            </div>
            {riskProfile && <div style={{fontSize:11,color:"var(--text-faint)"}}>Trading Style: <span style={{color:"var(--text-muted)",fontWeight:600}}>{riskProfile}</span></div>}
          </div>

          {/* Divider */}
          <div style={{width:1,height:72,background:"var(--border)",flexShrink:0}}/>

          {/* ROI */}
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Total ROI</div>
            <div style={{fontSize:26,fontWeight:900,color:roi>=0?"var(--success)":"var(--danger)"}}>
              {roi>=0?"+":""}{roi.toFixed(1)}%
            </div>
            <div style={{fontSize:10,color:"var(--text-faint)"}}>All-time closed trades</div>
          </div>

          {/* Flag count summary */}
          {flags.length > 0 && (
            <>
              <div style={{width:1,height:72,background:"var(--border)",flexShrink:0}}/>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Risk Flags</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {flags.filter(f=>f.severity==="CRITICAL").length>0 && (
                    <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,
                      background:"var(--flag-critical-bg)",color:"var(--flag-critical-text)",border:"1px solid var(--flag-critical-border)"}}>
                      {flags.filter(f=>f.severity==="CRITICAL").length} Critical
                    </span>
                  )}
                  {flags.filter(f=>f.severity==="MAJOR").length>0 && (
                    <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,
                      background:"rgba(245,158,11,0.12)",color:"var(--warning)",border:"1px solid rgba(245,158,11,0.3)"}}>
                      {flags.filter(f=>f.severity==="MAJOR").length} Major
                    </span>
                  )}
                  {minorFlags.length>0 && (
                    <span style={{fontSize:11,fontWeight:600,padding:"3px 10px",borderRadius:20,
                      background:"rgba(255,255,255,0.05)",color:"var(--text-faint)",border:"1px solid var(--border)"}}>
                      {minorFlags.length} Minor
                    </span>
                  )}
                </div>
                {flags.length===0 && <div style={{fontSize:12,color:"var(--success)",fontWeight:700}}> No flags</div>}
              </div>
            </>
          )}
        </div>
      </div>

      <SmartProfileReportCard smartReport={smartReport} />

      {/* ── SMART REPORT (consolidated) — DD + Flags + Outlook + Summary ── */}
      <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:14,padding:"20px 22px"}}>
        <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:16}}>
          Smart Report
        </div>

        {/* Row 1: Current DD + Active Flags */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
          <div>
            <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>
              Current Drawdown State
            </div>
            <DDSparkline currentDD={currentDD} maxDD={maxDD} ddTrend={ddTrend} />
          </div>
          <div>
            <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>
              Active Risk Flags
            </div>
            {activeFlags.length===0 ? (
              <div style={{display:"flex",flexDirection:"column",gap:6,paddingTop:4}}>
                <div style={{fontSize:13,fontWeight:700,color:"var(--success)"}}>No critical or major flags</div>
                <div style={{fontSize:11,color:"var(--text-faint)"}}>This account has a clean risk profile</div>
              </div>
            ) : activeFlags.map((f,i)=>{
              const st = severityStyle(f.severity);
              return (
                <div key={i} style={{background:st.bg,border:`1px solid ${st.border}`,borderRadius:8,padding:"10px 12px",marginBottom:6}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                    <span style={{fontSize:12,fontWeight:700,color:st.color}}>{f.soft_title||f.title||f.name}</span>
                    <span style={{fontSize:9,fontWeight:800,padding:"2px 6px",borderRadius:4,
                      background:`${st.color}22`,color:st.color,border:`1px solid ${st.color}44`}}>
                      {st.label}
                    </span>
                  </div>
                  <div style={{fontSize:11,color:"var(--text-faint)",lineHeight:1.5}}>{f.soft_desc||f.desc||f.description}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Row 2: Survivability / Scalability — compact inline */}
        <div style={{marginTop:18,paddingTop:16,borderTop:"1px solid var(--border)",display:"flex",gap:24,flexWrap:"wrap"}}>
          {[
            {l:"Survivability", s:survivability, sub:survMonths(survivability)},
            {l:"Scalability",   s:scalability,   sub:scaleLabel(scalability)},
          ].map(g=>{
            const sc = Number(g.s||0);
            const gc = sc>=80?"var(--success)":sc>=60?"var(--accent)":sc>=40?"var(--warning)":"var(--danger)";
            return (
              <div key={g.l} style={{display:"flex",alignItems:"baseline",gap:8,minWidth:160}}>
                <span style={{fontSize:20,fontWeight:900,color:gc}}>{sc.toFixed(0)}<span style={{fontSize:11,color:"var(--text-faint)",fontWeight:600}}>/100</span></span>
                <div style={{display:"flex",flexDirection:"column"}}>
                  <span style={{fontSize:11,fontWeight:700,color:"var(--text-muted)"}}>{g.l}</span>
                  <span style={{fontSize:10,color:"var(--text-faint)"}}>{g.sub}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Row 3: Data Summary */}
        <div style={{marginTop:18,paddingTop:16,borderTop:"1px solid var(--border)"}}>
          <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Data Summary</div>
          {(()=>{
            const avgRiskPct = Number(detail?.avgRiskPct||0);
            const avgLoss    = Number(detail?.avgLoss||0);
            const avgWin     = Number(detail?.avgWin||0);
            const peakUSD    = Number(detail?.peakEquityUSD||0);
            const isCent     = detail?.isCent||false;
            const scale      = Number(scalability||0);
            const mdd = Number(profile?.max_drawdown_pct||detail?.maxDD||0);
            const survDesc = `This account has historically experienced a max drawdown of ${mdd.toFixed(1)}% — ${mdd>=50?"with significant equity reduction, but account remained active":"account recovered from this level"}`;
            const scaleDesc = scale>=80?"Position sizes on this account have historically been large"
                             :scale>=60?"Position sizes on this account have historically been medium"
                             :scale>=40?"Position sizes on this account have historically been small"
                             :"Position sizes on this account have historically been very small";
            const criticalFlags = (flags||[]).filter(f=>f.severity==="CRITICAL");
            const majorFlags = (flags||[]).filter(f=>f.severity==="MAJOR");
            const flagDesc = criticalFlags.length>0
              ? `   ${criticalFlags.length} critical flag(s) detected: ${criticalFlags.map(f=>f.soft_title||f.title).join(", ")}`
              : majorFlags.length>0
              ? `Notable: ${majorFlags.map(f=>f.soft_title||f.title).join(", ")}`
              : "No significant risk flags detected on this account";
            return (
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"4px 24px"}}>
                  {[
                    {l:"Avg risk/trade", v:`${avgRiskPct.toFixed(2)}% of equity`},
                    {l:"Avg loss/trade", v:`$${avgLoss.toFixed(2)}`},
                    {l:"Avg win/trade",  v:`$${avgWin.toFixed(2)}`},
                    {l:"Peak equity",    v:`$${peakUSD.toFixed(2)}${isCent?" (cent acct)":""}`},
                  ].map(x=>(
                    <div key={x.l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                      padding:"4px 0",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                      <span style={{fontSize:11,color:"var(--text-faint)"}}>{x.l}</span>
                      <span style={{fontSize:11,fontWeight:700,color:"var(--text-main)"}}>{x.v}</span>
                    </div>
                  ))}
                </div>
                <div style={{marginTop:4,fontSize:11,color:"var(--text-faint)",lineHeight:1.6}}>
                  <div>• {survDesc}</div>
                  <div style={{marginTop:4}}>• {scaleDesc}</div>
                  <div style={{marginTop:4,color:criticalFlags.length>0?"var(--danger)":majorFlags.length>0?"var(--warning)":"var(--success)"}}>• {flagDesc}</div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ── LAYER 4 — Advanced (expandable) ──────────────────────── */}
      <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:14,overflow:"hidden"}}>
        <button onClick={()=>setShowAdvanced(v=>!v)}
          style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",
            padding:"14px 20px",background:"transparent",border:"none",cursor:"pointer",
            color:"var(--text-muted)",fontSize:13,fontWeight:700}}>
          <span>Advanced Analysis {showAdvanced?"▲":"▼"}</span>
          <span style={{display:"flex",alignItems:"center",gap:8,fontSize:11,fontWeight:400,color:"var(--text-faint)"}}>
            {(()=>{
              const critCount = flags.filter(f=>f.severity==="CRITICAL").length;
              const minorCount = flags.filter(f=>f.severity==="MINOR").length;
              if(critCount===0 && minorCount===0) return null;
              return (
                <span style={{display:"flex",gap:6}}>
                  {critCount>0 && <span style={{fontWeight:700,color:"var(--danger)"}}>{critCount} Critical</span>}
                  {minorCount>0 && <span style={{color:"var(--text-faint)"}}>{minorCount} Minor</span>}
                </span>
              );
            })()}
            <span>P1–P7 Pillars · SignalSync · Flag Detail</span>
          </span>
        </button>

        {showAdvanced && (
          <div style={{padding:"0 20px 20px",borderTop:"1px solid var(--border)"}}>

            {/* P1-P7 Pillars */}
            {pillars.length > 0 && (
              <div style={{marginTop:16,marginBottom:20}}>
                <div style={{fontSize:11,color:"var(--text-faint)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:12}}>AlphaRank™ Pillar Breakdown</div>
                <div style={{display:"flex",gap:16,alignItems:"flex-start"}}>
                  <div style={{flex:1}}>
                    {pillars.map(p=>(
                      <div key={p.code} style={{marginBottom:10}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                          <span style={{fontSize:12,fontWeight:600,color:"var(--text-muted)"}}>{p.name}</span>
                          <span style={{fontSize:12,fontWeight:800,color:"var(--text-main)"}}>{Number(p.score||0).toFixed(0)}</span>
                        </div>
                        <div style={{height:4,background:"rgba(255,255,255,0.06)",borderRadius:2,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${Math.min(p.score,100)}%`,
                            background:"var(--accent)",borderRadius:2,
                            opacity:0.35+Math.min(p.score,100)/100*0.65}}/>
                        </div>
                        {p.reason && <div style={{fontSize:10,color:"var(--text-faint)",marginTop:2}}>{p.reason}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* SignalSync LIVE vs PLAN */}
            {signalSyncStatus==="ACTIVE" && (
              <div style={{marginBottom:20,padding:"16px",background:"rgba(78,154,241,0.06)",border:"1px solid rgba(78,154,241,0.2)",borderRadius:10}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                  <span style={{fontSize:11,fontWeight:800,padding:"3px 10px",borderRadius:20,
                    background:"rgba(78,154,241,0.15)",color:"var(--accent)",border:"1px solid rgba(78,154,241,0.3)"}}>
                    ◆ SignalSync™ ACTIVE
                  </span>
                  {signalSyncStrategy && <span style={{fontSize:11,color:"var(--text-faint)"}}>Strategy: <strong style={{color:"var(--text-muted)"}}>{signalSyncStrategy}</strong></span>}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  <div style={{background:"var(--bg-elevated)",borderRadius:8,padding:"12px 14px",border:"1px solid var(--border)"}}>
                    <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:700,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.06em"}}>◆ PLAN (SL/TP Strict)</div>
                    {[
                      {l:"ROI",         v:`${planROI>=0?"+":""}${planROI.toFixed(1)}%`, c:planROI>=0?"var(--success)":"var(--danger)"},
                      {l:"Win Rate",    v:`${planWinRate.toFixed(1)}%`},
                      {l:"Max DD",      v:`${planMaxDD.toFixed(1)}%`,  c:planMaxDD>20?"var(--danger)":planMaxDD>10?"var(--warning)":"var(--success)"},
                      {l:"Trades",      v:planTotalTrades},
                    ].map(s=>(
                      <div key={s.l} style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                        <span style={{fontSize:11,color:"var(--text-faint)"}}>{s.l}</span>
                        <span style={{fontSize:11,fontWeight:700,color:s.c||"var(--text-main)"}}>{s.v}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{background:"var(--bg-elevated)",borderRadius:8,padding:"12px 14px",border:"1px solid var(--border)"}}>
                    <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:700,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.06em"}}>● LIVE (Actual)</div>
                    {[
                      {l:"ROI",         v:`${roi>=0?"+":""}${roi.toFixed(1)}%`, c:roi>=0?"var(--success)":"var(--danger)"},
                      {l:"Win Rate",    v:`${Number(profile?.win_rate||0).toFixed(1)}%`},
                      {l:"Max DD",      v:`${maxDD.toFixed(1)}%`, c:maxDD>20?"var(--danger)":maxDD>10?"var(--warning)":"var(--success)"},
                      {l:"Trades",      v:Number(profile?.total_trades||0)},
                    ].map(s=>(
                      <div key={s.l} style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                        <span style={{fontSize:11,color:"var(--text-faint)"}}>{s.l}</span>
                        <span style={{fontSize:11,fontWeight:700,color:s.c||"var(--text-main)"}}>{s.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{fontSize:11,color:"var(--text-faint)",marginTop:10,lineHeight:1.5}}>
                  PLAN simulates disciplined SL/TP exits only. LIVE reflects actual execution including manual closes.
                </div>
              </div>
            )}

            {/* All flags detail */}
            {flags.length > 0 && (
              <div>
                <div style={{fontSize:11,color:"var(--text-faint)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>All Risk Flags ({flags.length})</div>
                {flags.map((f,i)=>{
                  const st = severityStyle(f.severity);
                  return (
                    <div key={i} style={{background:st.bg,border:`1px solid ${st.border}`,borderRadius:8,padding:"10px 12px",marginBottom:6}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                        <span style={{fontSize:12,fontWeight:700,color:st.color}}>{f.soft_title||f.title||f.name}</span>
                        <span style={{fontSize:9,fontWeight:800,padding:"2px 6px",borderRadius:4,
                          background:`${st.color}22`,color:st.color,border:`1px solid ${st.color}44`}}>
                          {st.label}
                        </span>
                      </div>
                      <div style={{fontSize:11,color:"var(--text-faint)",lineHeight:1.5}}>{f.soft_desc||f.desc||f.description}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function TraderProfile() {
  usePageTitle("Trader Profile");
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copyModal, setCopyModal] = useState(false);
  const [investorOnlyModal, setInvestorOnlyModal] = useState(false);
  const userRole = localStorage.getItem("user_role") || "trader";
  const [copyForm, setCopyForm] = useState({});
  const [copyDone, setCopyDone] = useState(false);
  const [tab, setTab] = useState("overview");

  const [detail, setDetail] = useState(null);
  useEffect(()=>{
    const token = localStorage.getItem("access_token")||localStorage.getItem("token")||"";
    fetch(`/api/investor/trader-profile/${accountId}`, { headers:{ Authorization:`Bearer ${token}` }})
      .then(r=>r.json()).then(d=>{ setData(d); setLoading(false); });
    fetch(`/api/public/trader/${accountId}`)
      .then(r=>r.json()).then(d=>{ if(d.ok) setDetail(d); });
  },[accountId]);

  const [copyError, setCopyError] = useState("");
  const [copyMode, setCopyMode] = useState("LIVE");
  const doSubscribe = async () => {
    setCopyError("");
    const token = localStorage.getItem("access_token")||localStorage.getItem("token")||"";
    const r = await fetch("/api/investor/copy-trader-subscribe",{
      method:"POST", headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
      body: JSON.stringify({ traderAccountId:accountId, followerAccountId:selectedFollowerAccount, copyMode:copyMode })
    }).then(r=>r.json());
    if(r.ok) {
      setCopyDone(true);
      setCopyModal(false);
    } else if(r.error === "no_account") {
      setCopyModal(false);
      setNoAccountModal(true);
    } else {
      setCopyError(r.message || r.error || "Failed to subscribe");
    }
  };
  const [noAccountModal, setNoAccountModal] = useState(false);
  const [investorAccounts, setInvestorAccounts] = useState([]);
  const [selectedFollowerAccount, setSelectedFollowerAccount] = useState("");

  if(loading) return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ color:C.muted, fontSize:14 }}>Loading trader profile...</div>
    </div>
  );

  if(!data?.ok) return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ color:"var(--text-muted)", fontSize:14 }}>Trader not found</div>
    </div>
  );

  const { profile, pillars: pillarsRaw, pairs: pairsRaw, risk_flags_raw, statistics, tier, display_account_number, hide_trade_details, holding_period, timezone, preferred_instruments: prefInstrumentsRaw, smart_report } = data;
  let prefInstruments = [];
  try { prefInstruments = JSON.parse(prefInstrumentsRaw||"[]"); } catch(e){}
  const pillars = Array.isArray(pillarsRaw) ? pillarsRaw : [];
  const pairs = Array.isArray(pairsRaw) ? pairsRaw : [];
  const gradeColor = profile.grade?.startsWith("A")?"var(--grade-a)":profile.grade?.startsWith("B")?"var(--grade-b)":profile.grade?.startsWith("C")?"var(--grade-c)":"var(--grade-f)";
  let riskFlags = [];
  try { riskFlags = JSON.parse(risk_flags_raw||"[]"); } catch(e){}

  const tabs = [["overview","Overview"],["performance"," Performance"],["pairs"," Per Pair"],["history"," Trade History"],["profile"," Profile"]];

  return (
    <div style={{ minHeight:"100vh", background:C.bg, color:C.text, fontFamily:"Inter,sans-serif" }}>
      {/* Header */}
      <div style={{ background:C.card, borderBottom:`1px solid ${C.border}`, padding:"14px 24px",
        display:"flex", alignItems:"center", gap:16 }}>
        {tier && (
          <span style={{fontSize:11,fontWeight:800,padding:"3px 10px",borderRadius:10,
            background: tier==="INSTITUTIONAL"?"rgba(168,85,247,0.2)":tier==="PROFESSIONAL"?"rgba(78,154,241,0.2)":tier==="SEMI-PRO"?"rgba(29,209,161,0.2)":tier==="RETAIL"?"rgba(251,191,36,0.2)":"rgba(156,163,175,0.2)",
            color: tier==="INSTITUTIONAL"?"#a855f7":tier==="PROFESSIONAL"?"#4e9af1":tier==="SEMI-PRO"?"var(--success)":tier==="RETAIL"?"var(--warning)":"var(--text-muted)",
            border:"1px solid currentColor"}}>
            {tier}
          </span>
        )}
        <button onClick={()=>navigate(-1)} style={{ background:"rgba(255,255,255,0.05)",
          border:`1px solid ${C.border}`, color:C.muted, borderRadius:8,
          padding:"6px 14px", fontSize:12, cursor:"pointer" }}>Back</button>
        <div>
          <div style={{ fontSize:16, fontWeight:800, color:C.text }}>
            {profile.nickname || "Trader"}
          </div>
          <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>
            {profile.trader_name ? `by ${profile.trader_name} • ` : ""}{profile.broker} • {profile.platform}
          </div>
          {profile.strategy && (
            <div style={{ fontSize:12, color:C.muted, marginTop:6, fontStyle:"italic", maxWidth:500 }}>
              "{profile.strategy}"
            </div>
          )}
        </div>
        <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:12 }}>
          {copyDone ? (
            <div style={{ fontSize:13, fontWeight:700, color:"var(--success)" }}>Copying this trader</div>
          ) : (
            <button onClick={()=>{ if(userRole!=="investor"){ setInvestorOnlyModal(true); } else {
        const token = localStorage.getItem("access_token")||localStorage.getItem("token")||"";
        Promise.all([
          fetch("/api/trader/accounts", { headers:{ Authorization:`Bearer ${token}` }}).then(r=>r.json()),
          fetch("/api/investor/copy-trader-subscriptions", { headers:{ Authorization:`Bearer ${token}` }}).then(r=>r.json()),
        ]).then(([accData, subData]) => {
          const allAccs = (accData.accounts||[]).filter(a=>a.status==="active");
          const activeSubs = (subData.subscriptions||[]).filter(s=>s.status==="ACTIVE");
          const subscribedIds = new Set(activeSubs.filter(s=>s.traderId===accountId).map(s=>s.followerAccountId));
          const availableAccs = allAccs.filter(a=>!subscribedIds.has(a.id));
          setInvestorAccounts(availableAccs);
          setSelectedFollowerAccount(availableAccs[0]?.id||"");
          if(availableAccs.length===0) setCopyError("All your accounts are already copying this trader.");
          setCopyModal(true);
        });
      } }} style={{ background:C.accent, color:"#fff",
              border:"none", borderRadius:8, padding:"8px 20px", fontWeight:700, fontSize:13, cursor:"pointer" }}>
               Copy {profile.nickname || profile.strategy || profile.account_number || "Trader"}
            </button>
          )}
        </div>
      </div>

      <div style={{ maxWidth:1100, margin:"0 auto", padding:"24px 20px" }}>
        {/* Stats Bar */}
        <div style={{ display:"flex", gap:12, marginBottom:20, flexWrap:"wrap" }}>
          {[
            { l:"Win Rate",      v:fmtPct(profile.win_rate) },
            { l:"Total Trades",  v:profile.total_trades },
            { l:"Profit Factor", v:fmt2(profile.profit_factor) },
            { l:"Max Drawdown",  v:fmtPct(profile.max_drawdown_pct),
              c:profile.max_drawdown_pct>20?"var(--danger)":profile.max_drawdown_pct>10?"var(--warning)":"var(--success)" },
            { l:"Net P&L", v:fmtDollarSign(profile.net_profit),
              c:profile.net_profit>=0?"var(--success)":"var(--danger)" },
            { l:"ROI", v:(profile.roi>=0?"+":"")+fmtPct(Math.abs(profile.roi||0)),
              c:profile.roi>=0?"var(--success)":"var(--danger)" },
          ].map(s=>(
            <div key={s.l} style={{ flex:1, minWidth:110, background:C.card,
              border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px" }}>
              <div style={{ fontSize:10, color:"var(--text-faint)", fontWeight:600,
                letterSpacing:"0.07em", textTransform:"uppercase", marginBottom:4 }}>{s.l}</div>
              <div style={{ fontSize:18, fontWeight:700,
                color:s.c||"var(--text-main)" }}>{s.v}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", gap:6, marginBottom:20 }}>
          {tabs.map(([id,label])=>(
            <button key={id} onClick={()=>setTab(id)} style={{ padding:"8px 18px", borderRadius:8,
              border:"none", cursor:"pointer", fontSize:13, fontWeight:700,
              background:tab===id?C.accent:"rgba(255,255,255,0.05)",
              color:tab===id?"#fff":C.muted, borderBottom:tab===id?`2px solid ${C.accent}`:"none" }}>
              {label}
            </button>
          ))}
        </div>

        {/* Overview Tab — 4-Layer Smart View */}
        {tab==="overview" && (
          <SmartView profile={profile} detail={detail} riskFlags={riskFlags} pillars={pillars} colors={C} cardStyle={card} smartReport={smart_report} />
        )}

        {/* Performance Tab */}
        {tab==="performance" && (
          <div>
            <PerformanceTab accountId={accountId} colors={C} cardStyle={card} createdAt={profile.created_at} deposit={profile?.total_deposit||0}
  detailStats={detail ? {
    totalTrades: detail.totalTrades||0,
    winningTrades: detail.winningTrades||0,
    losingTrades: detail.losingTrades||0,
    winRate: detail.winRate||0,
    profitFactor: detail.profitFactor||0,
    avgWin: detail.avgWin||0,
    avgLoss: detail.avgLoss||0,
    riskReward: detail.riskReward||0,
    expectancy: detail.expectancy||0,
    maxDrawdown: detail.maxDD||0,
  } : null}
/>
          </div>
        )}

        {/* Per Pair Tab */}
        {tab==="pairs" && (
          <div style={card}>
            <div style={{ fontSize:13, fontWeight:800, color:C.text, marginBottom:16 }}>Performance Per Trading Pair</div>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ borderBottom:`1px solid ${C.border}` }}>
                  {["Pair","Trades","Win%","Avg RR","Net P&L","Flags"].map(h=>(
                    <th key={h} style={{ textAlign:"left", padding:"8px 10px", fontSize:11,
                      color:C.muted, fontWeight:700 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pairs.filter(p=>(p.trades||0)>=20).map((p,i)=>{
                  return (
                    <tr key={i} style={{ borderBottom:`1px solid rgba(255,255,255,0.04)` }}>
                      <td style={{ padding:"10px", fontSize:13, fontWeight:800, color:C.text }}>{p.symbol}</td>
                      <td style={{ padding:"10px", fontSize:13, color:C.text }}>{p.trades}</td>
                      <td style={{ padding:"10px", fontSize:13, color:C.muted }}>{((p.win_rate||p.winRate)||0).toFixed(1)}%</td>
                      <td style={{ padding:"10px", fontSize:13, color:C.text }}>{(p.avg_rr||p.avgRR)>0?(p.avg_rr||p.avgRR).toFixed(2):"—"}</td>
                      <td style={{ padding:"10px", fontSize:13, fontWeight:700, color:C.text }}>
                        {(p.net_pnl??p.netPnl??p.net_profit??0)>=0?"+":""}{(p.net_pnl??p.netPnl??p.net_profit??0).toFixed(2)}
                      </td>
                      <td style={{ padding:"10px", fontSize:11 }}>
                        {Array.isArray(p.flags) && p.flags.map((f,fi)=>(
                          <span key={fi} style={{ marginRight:4, padding:"2px 7px", borderRadius:10, fontSize:10,
                            background:"rgba(100,100,120,0.15)", border:"1px solid rgba(150,150,170,0.2)",
                            color:"var(--text-muted)" }}>
                            {f.soft_title||f.name||f.title}
                          </span>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Trade History Tab */}
        {tab==="history" && (
          <TradeHistory accountId={accountId} />
        )}
      </div>

        {/* Profile Tab */}
        {tab==="profile" && (
          <div style={card}>
            <div style={{ fontSize:13, fontWeight:800, color:C.text, marginBottom:20 }}> Trader Profile</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:6 }}>Display Name</div>
                <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{profile.trader_name || "-"}</div>
              </div>
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:6 }}>Account Name</div>
                <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{profile.nickname || "Trader"}</div>
              </div>
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:6 }}>Country / Region</div>
                <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{profile.country ? " "+profile.country : "-"}</div>
              </div>
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:6 }}>Broker</div>
                <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{profile.broker} • {profile.platform}</div>
              </div>
            </div>
            {profile.bio && (
              <div style={{ marginTop:20 }}>
                <div style={{ fontSize:11, color:C.muted, marginBottom:8 }}>Bio & Trading Style</div>
                <div style={{ fontSize:13, color:C.text, lineHeight:1.7, background:"rgba(255,255,255,0.03)",
                  borderRadius:8, padding:"14px 16px", border:`1px solid ${C.border}` }}>
                  {profile.bio}
                </div>
              </div>
            )}
            {!profile.bio && !profile.country && (
              <div style={{ fontSize:13, color:"var(--text-faint)", textAlign:"center", padding:"40px 0" }}>
                This trader has not filled in their profile yet.
              </div>
            )}
            {/* Trading Preferences */}
            {(prefInstruments.length > 0 || holding_period || timezone) && (
              <div style={{ marginTop:24, paddingTop:20, borderTop:"1px solid var(--border-subtle)" }}>
                <div style={{ fontSize:13, fontWeight:600, color:"var(--text-main)", marginBottom:16 }}>Trading Preferences</div>
                <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                  {prefInstruments.length > 0 && (
                    <div>
                      <div style={{ fontSize:11, color:"var(--text-faint)", marginBottom:8, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.07em" }}>Preferred Instruments</div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                        {prefInstruments.map(inst => {
                          const labels = { gold:"Gold (XAUUSD)", forex_major:"Forex Majors", forex_minor:"Forex Minors",
                            indices:"Indices", oil:"Oil / Commodities", crypto:"Crypto", stocks:"Stocks" };
                          return (
                            <span key={inst} style={{ padding:"4px 12px", borderRadius:20, fontSize:11, fontWeight:600,
                              background:"var(--bg-elevated)", color:"var(--text-muted)", border:"1px solid var(--border)" }}>
                              {labels[inst]||inst}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div style={{ display:"flex", gap:24 }}>
                    {holding_period && (
                      <div>
                        <div style={{ fontSize:11, color:"var(--text-faint)", marginBottom:4, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.07em" }}>Holding Period</div>
                        <div style={{ fontSize:13, fontWeight:600, color:"var(--text-main)" }}>
                          {{ scalping:"Scalping (< 1hr)", intraday:"Intraday", swing:"Swing (days)", position:"Position (weeks+)" }[holding_period] || holding_period}
                        </div>
                      </div>
                    )}
                    {timezone && (
                      <div>
                        <div style={{ fontSize:11, color:"var(--text-faint)", marginBottom:4, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.07em" }}>Timezone</div>
                        <div style={{ fontSize:13, fontWeight:600, color:"var(--text-main)" }}>{timezone}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      {/* Copy Modal */}
      {/* Investor Only Modal */}
      {investorOnlyModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:2000,
          display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={()=>setInvestorOnlyModal(false)}>
          <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",
            borderRadius:16,padding:32,width:420,maxWidth:"90vw",textAlign:"center"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:32,marginBottom:12}}></div>
            <div style={{fontSize:18,fontWeight:800,color:"var(--text-main)",marginBottom:8}}>
              Investor Account Required
            </div>
            <div style={{fontSize:13,color:"#8c92b0",marginBottom:8}}>
              To copy traders, you need an Investor account.
            </div>
            <div style={{fontSize:12,color:"var(--danger)",marginBottom:24,
              background:"rgba(248,113,113,0.1)",borderRadius:8,padding:"8px 12px"}}>
              You must register with a <strong>new email address</strong> to create an Investor account.
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"center"}}>
              <button onClick={()=>setInvestorOnlyModal(false)}
                style={{padding:"10px 20px",borderRadius:8,border:"1px solid rgba(255,255,255,0.1)",
                  background:"transparent",color:"#8c92b0",fontSize:13,cursor:"pointer"}}>
                Close
              </button>
              <button onClick={()=>window.location.href="/register?role=investor"}
                style={{padding:"10px 24px",borderRadius:8,border:"none",
                  background:"var(--accent)",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                Register as Investor
              </button>
            </div>
          </div>
        </div>
      )}

      {copyModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:1000,
          display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"var(--bg-surface)", borderRadius:14, border:`1px solid var(--border)`,
            padding:"28px 32px", minWidth:340, maxWidth:420 }}>
            <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:4 }}>
              Copy {profile.nickname || profile.strategy || profile.account_number || "Trader"}
            </div>
            <div style={{ fontSize:12, color:C.muted, marginBottom:20 }}>
              {profile.nickname || "Trader"}{profile.trader_name ? ` • by ${profile.trader_name}` : ""}
            </div>
            {profile.signal_sync_status === "ACTIVE" && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:12, color:"var(--text-muted)", marginBottom:8, fontWeight:600 }}>Execution mode:</div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={()=>setCopyMode("LIVE")}
                    style={{ flex:1, textAlign:"left", padding:"10px 12px", borderRadius:8, cursor:"pointer",
                      border: copyMode==="LIVE" ? "1.5px solid var(--success)" : "1px solid var(--border)",
                      background: copyMode==="LIVE" ? "rgba(34,197,94,0.08)" : "var(--bg-elevated)" }}>
                    <div style={{ fontSize:12, fontWeight:700, color: copyMode==="LIVE" ? "var(--success)" : C.text, marginBottom:3 }}>● LIVE</div>
                    <div style={{ fontSize:10.5, color:C.muted, lineHeight:1.4 }}>Mirrors their actual execution, including manual closes.</div>
                  </button>
                  <button onClick={()=>setCopyMode("PLAN")}
                    style={{ flex:1, textAlign:"left", padding:"10px 12px", borderRadius:8, cursor:"pointer",
                      border: copyMode==="PLAN" ? "1.5px solid var(--accent)" : "1px solid var(--border)",
                      background: copyMode==="PLAN" ? "rgba(78,154,241,0.08)" : "var(--bg-elevated)" }}>
                    <div style={{ fontSize:12, fontWeight:700, color: copyMode==="PLAN" ? "var(--accent)" : C.text, marginBottom:3 }}>◆ PLAN</div>
                    <div style={{ fontSize:10.5, color:C.muted, lineHeight:1.4 }}>Follows their disciplined plan — exits only at SL/TP.</div>
                  </button>
                </div>
              </div>
            )}
            <div style={{ background:"rgba(78,154,241,0.1)", border:`1px solid rgba(78,154,241,0.3)`,
              borderRadius:8, padding:"12px 16px", marginBottom:20, fontSize:13, color:C.text, lineHeight:1.6 }}>
              <div style={{ fontWeight:700, marginBottom:6 }}>ℹ   AUM-Based Copy Trading</div>
              <div style={{ color:C.muted }}>Lot size will be calculated automatically based on your allocation settings.</div>
              <div style={{ color:C.muted, marginTop:4 }}>Configure allocation % in <strong style={{color:C.accent}}>Investor Dashboard → Copy Settings</strong> before starting.</div>
            </div>
            {investorAccounts.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:12, color:"var(--text-muted)", marginBottom:6, fontWeight:600 }}>Copy using account:</div>
                <select value={selectedFollowerAccount} onChange={e=>setSelectedFollowerAccount(e.target.value)}
                  style={{ width:"100%", background:"var(--bg-elevated)", border:"1px solid var(--border)",
                    borderRadius:8, padding:"8px 12px", color:"var(--text-main)", fontSize:13, cursor:"pointer" }}>
                  {investorAccounts.map(a=>(
                    <option key={a.id} value={a.id}>
                      {a.account_number} — {a.broker} ({a.platform})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {copyError && (
              <div style={{background:"rgba(248,113,113,0.1)",border:"1px solid rgba(248,113,113,0.3)",
                borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12,color:"var(--danger)"}}>
                {copyError}
              </div>
            )}
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={doSubscribe} style={{ flex:1, padding:"10px", background:C.accent,
                color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:13, cursor:"pointer" }}>
                 Start Copying
              </button>
              <button onClick={()=>setCopyModal(false)} style={{ flex:1, padding:"10px",
                background:"rgba(255,255,255,0.05)", color:C.muted,
                border:`1px solid ${C.border}`, borderRadius:8, fontWeight:700, fontSize:13, cursor:"pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No Account Modal */}
      {noAccountModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:1000,
          display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"var(--bg-surface)",borderRadius:14,border:`1px solid var(--border)`,
            padding:"28px 32px",minWidth:340,maxWidth:420,textAlign:"center"}}>
            <div style={{fontSize:32,marginBottom:12}}></div>
            <div style={{fontSize:15,fontWeight:800,color:C.text,marginBottom:8}}>
              Link a Trading Account First
            </div>
            <div style={{fontSize:13,color:C.muted,marginBottom:20,lineHeight:1.6}}>
              To copy traders, you need to link your MT5/MT4 broker account and install the CrunchAlpha Investor EA.
            </div>
            <div style={{background:"rgba(78,154,241,0.08)",border:"1px solid rgba(78,154,241,0.2)",
              borderRadius:8,padding:"12px 16px",marginBottom:20,textAlign:"left"}}>
              <div style={{fontSize:12,fontWeight:700,color:C.accent,marginBottom:8}}>Steps:</div>
              <div style={{fontSize:12,color:C.muted,lineHeight:1.8}}>
                1  ⃣ Go to <strong style={{color:C.text}}>Trader Dashboard → + Add Account</strong><br/>
                2  ⃣ Link your MT5/MT4 broker account<br/>
                3  ⃣ Set allocation % in <strong style={{color:C.text}}>Investor Dashboard → Copy Settings</strong><br/>
                4  ⃣ Come back here and start copying!
              </div>
            </div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>{ setNoAccountModal(false); window.location.href="/trader"; }}
                style={{flex:1,padding:"10px",background:C.accent,color:"#fff",border:"none",
                  borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer"}}>
                 Add Account Now
              </button>
              <button onClick={()=>setNoAccountModal(false)}
                style={{flex:1,padding:"10px",background:"rgba(255,255,255,0.05)",color:C.muted,
                  border:`1px solid ${C.border}`,borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer"}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MonthlyChart({ accountId, accountCreatedAt, deposit }) {
  const [data, setData] = useState([]);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());

  useEffect(() => {
    if (!accountId) return;
    const token = localStorage.getItem("access_token")||localStorage.getItem("token")||"";
    fetch(`/api/investor/trader-monthly-performance?account_id=${accountId}`, {
      headers:{ Authorization:`Bearer ${token}` }
    }).then(r=>r.json()).then(d=>{
      if(d.months) setData(d.months);
    });
  }, [accountId]);

  // Year range: dari tahun data terlama atau accountCreatedAt, sampai sekarang
  const dataYears = [...new Set(data.map(m => m.year))];
  const createdYear = accountCreatedAt ? new Date(accountCreatedAt).getFullYear() : now.getFullYear();
  const startYear = dataYears.length > 0 ? Math.min(...dataYears, createdYear) : createdYear;
  const years = [];
  for(let y = now.getFullYear(); y >= startYear; y--) years.push(y);

  const MONTH_NAMES = ["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const dataMap = {};
  data.forEach(m => { dataMap[`${m.year}-${m.month}`] = m; });

  const filtered = [1,2,3,4,5,6,7,8,9,10,11,12].map(mIdx => {
    const key = `${year}-${MONTH_NAMES[mIdx]}`;
    const actual = dataMap[key];
    return {
      month: MONTH_NAMES[mIdx],
      profit: actual?.profit || 0,
      trades: actual?.trades || 0,
      wins: actual?.wins || 0,
      losses: actual?.losses || 0,
      hasData: !!actual
    };
  });

  const maxProfit = Math.max(...filtered.map(m=>Math.abs(m.profit)), 1);

  return (
    <div style={cardPad}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div style={{ fontSize:13, fontWeight:800, color:C.text }}> Monthly Performance</div>
        <select value={year} onChange={e=>setYear(Number(e.target.value))}
          style={{ background:C.elevated, border:`1px solid ${C.border}`, color:C.text,
            borderRadius:6, padding:"4px 8px", fontSize:12 }}>
          {years.map(y=><option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div style={{ display:"flex", gap:6, alignItems:"flex-end", height:120, overflowX:"auto" }}>
        {filtered.map(m=>{
          const pct = m.profit===0 ? 0 : Math.max((Math.abs(m.profit)/maxProfit)*100, 8);
          const positive = m.profit>=0;
          const cumulative = filtered.slice(0, filtered.indexOf(m)+1).reduce((s,x)=>s+x.profit,0);
          const roiPct = deposit>0 ? (cumulative/deposit*100) : 0;
          const profitLabel = m.profit===0?"—":((positive?"+$":"-$")+(Math.abs(m.profit)>=1000?(Math.abs(m.profit)/1000).toFixed(1)+"k":Math.abs(m.profit).toFixed(0)));
          return (
            <div key={m.month} style={{ flex:1, minWidth:36, display:"flex",
              flexDirection:"column", alignItems:"flex-end", gap:4 }}>
              <div style={{ fontSize:10, color:m.profit===0?C.muted:positive?"var(--success)":"var(--danger)", fontWeight:700 }}>
                {profitLabel}
              </div>
              {m.profit!==0 && deposit>0 && (
                <div style={{ fontSize:9, color:C.muted, fontWeight:600 }}>
                  {roiPct>=0?"+":""}{roiPct.toFixed(1)}%
                </div>
              )}
              <div style={{ width:"100%", height:`${pct}%`, borderRadius:"4px 4px 0 0",
                background:m.profit===0?"var(--bg-elevated)":positive?"var(--success)":"var(--danger)",
                opacity:m.profit===0?0.4:0.9, minHeight: m.profit===0?3:8 }} />
              <div style={{ fontSize:10, color:C.muted }}>{m.month}</div>
            </div>
          );
        })}
      </div>
      <div style={{ display:"flex", justifyContent:"space-between", marginTop:12, fontSize:11, color:C.muted }}>
        <span>Total months: {filtered.filter(m=>m.hasData).length}</span>
        <span>Profitable: {filtered.filter(m=>m.profit>0).length}</span>
        <span>Loss: {filtered.filter(m=>m.profit<0).length}</span>
      </div>
    </div>
  );
}

function PerPairTable({ perPairData }) {
  if (!perPairData?.pairs?.length) return (
    <div style={{ ...cardPad, textAlign:"center", color:C.muted, padding:40 }}>No per-pair data</div>
  );
  return (
    <div style={cardPad}>
      <div style={{ fontSize:13, fontWeight:800, color:C.text, marginBottom:14 }}>Performance Per Trading Pair</div>
      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr>{["Pair","Trades","Win%","Avg RR","Net P&L","Flags"].map(h=>(
              <th key={h} style={th}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {perPairData.pairs.filter(p=>(p.trades||0)>=20).map(p=>{
              return (
                <tr key={p.symbol}>
                  <td style={{...td,fontWeight:800}}>{p.symbol||"—"}</td>
                  <td style={td}>{p.trades||0}</td>
                  <td style={td}>{((p.win_rate||p.winRate)!=null)?(p.win_rate||p.winRate).toFixed(1)+"%":"—"}</td>
                  <td style={td}>{(p.avg_rr||p.avgRR)>0?(p.avg_rr||p.avgRR).toFixed(2):"—"}</td>
                  <td style={{...td,color:(p.net_pnl??p.netPnl??p.net_profit??0)>=0?"var(--success)":"var(--danger)",fontWeight:700}}>
                    {(p.net_pnl??p.netPnl??p.net_profit??0)>=0?"+":""}{(p.net_pnl??p.netPnl??p.net_profit??0).toFixed(2)}
                  </td>
                  <td style={td}>
                    {p.flags?.length>0 ? p.flags.map((f,i)=>(
                      <span key={i} style={{ fontSize:10, padding:"2px 6px", borderRadius:4, marginRight:4,
                        background:"rgba(100,100,120,0.15)", border:"1px solid rgba(150,150,170,0.2)",
                        color:"var(--text-muted)" }}>
                        {f.soft_title||f.name||f.title}
                      </span>
                    )) : <span style={{ color:C.muted }}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Trade History ────────────────────────────────────────────────────────────
function TradeHistory({ accountId }) {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(()=>{
    if(!accountId) return;
    setLoading(true);
    const token = localStorage.getItem("access_token")||localStorage.getItem("token")||"";
    fetch(`/api/investor/trader-trades?account_id=${accountId}&limit=${limit}&offset=${offset}`, {
      headers:{ Authorization:`Bearer ${token}` }
    }).then(r=>r.json()).then(d=>{ setTrades(d.trades||[]); setTotal(d.total||0); }).finally(()=>setLoading(false));
  },[accountId, limit, offset]);

  return (
    <div style={cardPad}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <div style={{ fontSize:13, fontWeight:800, color:C.text }}> Trade History</div>
        <select value={limit} onChange={e=>{ setLimit(Number(e.target.value)); setOffset(0); }}
          style={{ background:C.elevated, border:`1px solid ${C.border}`, color:C.text,
            borderRadius:6, padding:"4px 8px", fontSize:12 }}>
          {[20,50,100,200].map(n=><option key={n} value={n}>{n} per page</option>)}
        </select>
      </div>
      {loading ? <div style={{ color:C.muted, textAlign:"center", padding:20 }}>Loading...</div> : (
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr>
              {["Symbol","Type","Open","Close","Profit"].map(h=>(
                <th key={h} style={th}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {trades.map(t=>(
                <tr key={t.id}>
                  <td style={{...td,fontWeight:800}}>{t.symbol}</td>
                  <td style={{...td,color:C.muted,fontWeight:600,fontSize:11}}>
                    {t.type?.toUpperCase()}
                  </td>
                  <td style={{...td,fontSize:11,color:C.muted}}>{t.open_time?.slice(0,16).replace("T"," ")}</td>
                  <td style={{...td,fontSize:11,color:C.muted}}>{t.close_time?.slice(0,16).replace("T"," ")}</td>
                  <td style={{...td,fontWeight:800,color:t.profit>=0?"var(--success)":"var(--danger)"}}>
                    {t.profit>=0?"+":""}{fmt2(t.profit)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {trades.length===0 && <div style={{ textAlign:"center", color:C.muted, padding:24 }}>No trades found</div>}
      {total > 0 && (
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:14, flexWrap:"wrap", gap:8 }}>
          <div style={{ fontSize:12, color:C.muted }}>
            Showing {offset+1}–{Math.min(offset+limit, total)} of {total} trades
          </div>
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
            <button onClick={()=>setOffset(0)} disabled={offset===0}
              style={{ padding:"4px 10px", borderRadius:6, border:`1px solid ${C.border}`,
                background:offset===0?C.elevated:"rgba(102,126,234,0.15)",
                color:offset===0?C.muted:"#667eea", cursor:offset===0?"not-allowed":"pointer", fontSize:12 }}>«</button>
            <button onClick={()=>setOffset(Math.max(0,offset-limit))} disabled={offset===0}
              style={{ padding:"4px 10px", borderRadius:6, border:`1px solid ${C.border}`,
                background:offset===0?C.elevated:"rgba(102,126,234,0.15)",
                color:offset===0?C.muted:"#667eea", cursor:offset===0?"not-allowed":"pointer", fontSize:12 }}>‹ Prev</button>
            <span style={{ fontSize:12, color:C.muted }}>Page {Math.floor(offset/limit)+1} / {Math.ceil(total/limit)}</span>
            <button onClick={()=>setOffset(offset+limit)} disabled={offset+limit>=total}
              style={{ padding:"4px 10px", borderRadius:6, border:`1px solid ${C.border}`,
                background:offset+limit>=total?C.elevated:"rgba(102,126,234,0.15)",
                color:offset+limit>=total?C.muted:"#667eea", cursor:offset+limit>=total?"not-allowed":"pointer", fontSize:12 }}>Next ›</button>
            <button onClick={()=>setOffset((Math.ceil(total/limit)-1)*limit)} disabled={offset+limit>=total}
              style={{ padding:"4px 10px", borderRadius:6, border:`1px solid ${C.border}`,
                background:offset+limit>=total?C.elevated:"rgba(102,126,234,0.15)",
                color:offset+limit>=total?C.muted:"#667eea", cursor:offset+limit>=total?"not-allowed":"pointer", fontSize:12 }}>»</button>
          </div>
        </div>
      )}
        </div>
      )}
    </div>
  );
}

// ─── Weekly Bar Chart ────────────────────────────────────────────────────────
function getISOWeekMonday(week, year) {
  const jan4 = new Date(year, 0, 4);
  const startOfWeek1 = new Date(jan4);
  startOfWeek1.setDate(jan4.getDate() - (jan4.getDay() || 7) + 1);
  const monday = new Date(startOfWeek1);
  monday.setDate(startOfWeek1.getDate() + (week - 1) * 7);
  return monday;
}

// Generate all weeks (Mon) in a given month+year
function getWeeksInMonth(month, year) {
  const weeks = [];
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  // Find first Monday on or before firstDay
  let cur = new Date(firstDay);
  const dow = cur.getDay() || 7;
  cur.setDate(cur.getDate() - dow + 1);
  while (cur <= lastDay) {
    if (cur.getMonth() + 1 === month || cur <= lastDay) {
      // Get ISO week number for this Monday
      const jan4 = new Date(cur.getFullYear(), 0, 4);
      const startW1 = new Date(jan4);
      startW1.setDate(jan4.getDate() - (jan4.getDay() || 7) + 1);
      const weekNum = Math.round((cur - startW1) / (7 * 86400000)) + 1;
      const weekYear = cur >= new Date(cur.getFullYear(), 11, 29) ? cur.getFullYear() + 1
                     : cur < new Date(cur.getFullYear(), 0, 4) ? cur.getFullYear() - 1
                     : cur.getFullYear();
      // Only include if Monday is within or overlaps the month
      if (cur <= lastDay) weeks.push({ weekNum, weekYear, monday: new Date(cur) });
    }
    cur.setDate(cur.getDate() + 7);
  }
  return weeks;
}


function WeeklyChart({ accountId, accountCreatedAt, deposit }) {
  const [data, setData] = React.useState([]);
  const now = new Date();
  const [selYear, setSelYear] = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);

  React.useEffect(()=>{
    if(!accountId) return;
    const token = localStorage.getItem("access_token")||localStorage.getItem("token")||"";
    fetch(`/api/investor/trader-weekly-performance?account_id=${accountId}`,{ headers:{ Authorization:`Bearer ${token}` }})
      .then(r=>r.json()).then(d=>{
        if(d.weeks) setData(d.weeks);
      });
  },[accountId]);

  const MONTH_NAMES = ["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  // Year range: from account creation year to current year
  // Year range: dari tahun data terlama atau accountCreatedAt, sampai sekarang
  const dataYears = [...new Set(data.map(w => getISOWeekMonday(w.week, w.year).getFullYear()))];
  const createdYear = accountCreatedAt ? new Date(accountCreatedAt).getFullYear() : now.getFullYear();
  const startYear = dataYears.length > 0 ? Math.min(...dataYears, createdYear) : createdYear;
  const years = [];
  for(let y = now.getFullYear(); y >= startYear; y--) years.push(y);

  // All 12 months always shown
  const allMonths = [1,2,3,4,5,6,7,8,9,10,11,12];

  // Build week data: all weeks in selMonth+selYear, merged with actual data
  const weeksInMonth = getWeeksInMonth(selMonth, selYear);
  const dataMap = {};
  data.forEach(w => { dataMap[`${w.year}-${w.week}`] = w; });

  const filtered = weeksInMonth.map(({ weekNum, weekYear, monday }) => {
    const key = `${weekYear}-${weekNum}`;
    const actual = dataMap[key];
    return {
      week: weekNum,
      year: weekYear,
      monday,
      profit: actual?.profit || 0,
      trades: actual?.trades || 0,
      wins: actual?.wins || 0,
      losses: actual?.losses || 0,
      hasData: !!actual
    };
  });

  const maxP = Math.max(...filtered.map(w=>Math.abs(w.profit)), 1);

  const handleYearChange = (y) => { setSelYear(y); };

  return (
    <div style={cardPad}>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16 }}>
        <div style={{ fontSize:13,fontWeight:800,color:C.text }}> Weekly Performance</div>
        <div style={{ display:"flex",gap:6 }}>
          <select value={selMonth} onChange={e=>setSelMonth(Number(e.target.value))}
            style={{ background:C.elevated,border:`1px solid ${C.border}`,color:C.text,
            borderRadius:6,padding:"4px 8px",fontSize:12 }}>
            {allMonths.map(m=><option key={m} value={m}>{MONTH_NAMES[m]}</option>)}
          </select>
          <select value={selYear} onChange={e=>handleYearChange(Number(e.target.value))}
            style={{ background:C.elevated,border:`1px solid ${C.border}`,color:C.text,
            borderRadius:6,padding:"4px 8px",fontSize:12 }}>
            {years.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display:"flex",gap:4,alignItems:"flex-end",height:100,overflowX:"auto" }}>
        {filtered.map((w,i)=>{
          const pct = w.profit===0 ? 0 : Math.max((Math.abs(w.profit)/maxP)*90, 8);
          const pos = w.profit>=0;
          const dateLabel = w.monday.getDate()+"/"+(w.monday.getMonth()+1);
          const cumulative = filtered.slice(0, i+1).reduce((s,x)=>s+x.profit, 0);
          const roiPct = deposit>0 ? (cumulative/deposit*100) : 0;
          const profitLabel = w.profit===0?"—":((pos?"+$":"-$")+(Math.abs(w.profit)>=1000?(Math.abs(w.profit)/1000).toFixed(1)+"k":Math.abs(w.profit).toFixed(0)));
          return (
            <div key={i} style={{ flex:1,minWidth:44,display:"flex",flexDirection:"column",alignItems:"flex-end",gap:3 }}>
              <div style={{ fontSize:9,color:w.profit===0?C.muted:pos?"var(--success)":"var(--danger)",fontWeight:700 }}>
                {profitLabel}
              </div>
              {w.profit!==0 && deposit>0 && (
                <div style={{ fontSize:8,color:C.muted,fontWeight:600 }}>
                  {roiPct>=0?"+":""}{roiPct.toFixed(1)}%
                </div>
              )}
              <div style={{ width:"100%",height:`${pct}%`,borderRadius:"3px 3px 0 0",
                background:w.profit===0?"var(--bg-elevated)":pos?"var(--success)":"var(--danger)",
                opacity:w.profit===0?0.4:0.9, minHeight: w.profit===0?3:8 }} />
              <div style={{ fontSize:9,color:C.muted }}>W{w.week}</div>
              <div style={{ fontSize:8,color:C.muted,opacity:0.6 }}>{dateLabel}</div>
            </div>
          );
        })}
      </div>
      <div style={{ display:"flex",justifyContent:"space-between",marginTop:10,fontSize:11,color:C.muted }}>
        <span>Weeks: {filtered.filter(w=>w.hasData).length}</span>
        <span>Profitable: {filtered.filter(w=>w.profit>0).length}</span>
        <span>Trades: {filtered.reduce((s,w)=>s+w.trades,0)}</span>
      </div>
    </div>
  );
}


function EqCurveChart({pts,W,H,PL,PR,PT,PB,xOf,yOf,path,areaPath,lineColor,ticks,fmtMini,iH,iW}) {
  const [hover,setHover] = React.useState(null);
  const svgRef = React.useRef(null);
  const handleMove = e => {
    const rect = svgRef.current?.getBoundingClientRect();
    if(!rect) return;
    const svgX = (e.clientX-rect.left)/rect.width*W;
    const idx = Math.round((svgX-PL)/iW*(pts.length-1));
    const clamped = Math.max(0,Math.min(pts.length-1,idx));
    setHover({idx:clamped, x:xOf(clamped), y:yOf(pts[clamped].equity), pt:pts[clamped]});
  };
  return (
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:"auto",cursor:"crosshair"}}
      onMouseMove={handleMove} onMouseLeave={()=>setHover(null)}>
      <defs>
        <linearGradient id="pubEqGrad2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lineColor} stopOpacity="0.18"/>
          <stop offset="100%" stopColor={lineColor} stopOpacity="0"/>
        </linearGradient>
      </defs>
      {ticks.map((v,i)=>(
        <g key={i}>
          <line x1={PL} x2={W-PR} y1={yOf(v)} y2={yOf(v)} stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
          <text x={PL-4} y={yOf(v)+4} textAnchor="end" fontSize="9" fill="rgba(255,255,255,0.3)">{fmtMini(v)}</text>
        </g>
      ))}
      <path d={areaPath} fill="url(#pubEqGrad2)"/>
      <path d={path} fill="none" stroke={lineColor} strokeWidth="1.5"/>
      {hover && (
        <>
          <line x1={hover.x} x2={hover.x} y1={PT} y2={H-PB} stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="3,3"/>
          <circle cx={hover.x} cy={hover.y} r="4" fill={lineColor} stroke="var(--bg-surface)" strokeWidth="2"/>
          <rect x={Math.min(hover.x+8,W-130)} y={hover.y-28} width="120" height="36" rx="4"
            fill="var(--bg-elevated)" stroke="var(--border)" strokeWidth="1"/>
          <text x={Math.min(hover.x+14,W-124)} y={hover.y-12} fontSize="10" fill="rgba(255,255,255,0.5)">
            {hover.pt.time?.slice(0,10)||""}
          </text>
          <text x={Math.min(hover.x+14,W-124)} y={hover.y+4} fontSize="11" fontWeight="700" fill={lineColor}>
            ${fmt2(hover.pt.equity)}
          </text>
        </>
      )}
    </svg>
  );
}


function PublicEquityChart({ accountId }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [days, setDays] = React.useState(3650);
  const [hover, setHover] = React.useState(null);
  const svgRef = React.useRef(null);
  React.useEffect(() => {
    if (!accountId) return;
    setLoading(true);
    fetch(`/api/public/trader-equity-curve?account_id=${accountId}&days=${days}`)
      .then(r=>r.json()).then(d=>{ setData(d); setLoading(false); }).catch(()=>setLoading(false));
  }, [accountId, days]);
  const selStyle = {background:"var(--bg-elevated)",border:"1px solid var(--border)",color:"var(--text-main)",borderRadius:"var(--radius-md)",padding:"5px 10px",fontSize:12};
  const points = data?.points || [];
  const W=900,H=220,PAD={top:16,right:16,bottom:32,left:64};
  const innerW=W-PAD.left-PAD.right, innerH=H-PAD.top-PAD.bottom;
  const validPts=points;
  const equityVals=validPts.map(p=>p.equity), balanceVals=validPts.map(p=>p.balance);
  const maxEquityVals=validPts.map(p=>p.max_equity||p.equity);
  const minEquityVals=validPts.map(p=>p.min_equity||p.equity);
  const allVals=[...maxEquityVals,...balanceVals].filter(v=>v>0);
  const allMinVals=[...minEquityVals].filter(v=>v>0);
  const minV=allMinVals.length?Math.min(...allMinVals)*0.995:0;
  const maxV=allVals.length?Math.max(...allVals)*1.005:1;
  const xOf=i=>PAD.left+(i/Math.max(validPts.length-1,1))*innerW;
  const yOf=v=>PAD.top+innerH-((v-minV)/(maxV-minV||1))*innerH;
  const toPath=vals=>vals.map((v,i)=>`${i===0?"M":"L"}${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`).join(" ");
  const toFill=vals=>{ if(!vals.length)return""; const base=yOf(minV); return toPath(vals)+` L${xOf(vals.length-1).toFixed(1)},${base} L${xOf(0).toFixed(1)},${base} Z`; };
  const yTickVals=Array.from({length:6},(_,i)=>minV+(maxV-minV)*(i/5));
  const xTickCount=Math.min(6,points.length);
  const xTickIdxs=validPts.length<=1?[0]:Array.from({length:xTickCount},(_,i)=>Math.round(i*(validPts.length-1)/(xTickCount-1)));
  const fmtDate=str=>{ if(!str)return""; const d=new Date(str); return `${d.getDate()}/${d.getMonth()+1}`; };
  const fmtMoney=v=>fmtDollar(v);
  const growth=data?.growth_pct??0;
  const growthColor=growth>=0?"var(--success)":"var(--danger)";
  return (
    <div style={{background:"var(--bg-surface)",borderRadius:"var(--radius-lg)",border:"1px solid var(--border)",padding:20,marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Equity Growth Curve</div>
          {data&&points.length>1&&<span style={{fontSize:12,fontWeight:700,padding:"2px 8px",borderRadius:"var(--radius-sm)",background:growth>=0?"var(--success-soft)":"var(--danger-soft)",color:growthColor}}>{growth>=0?"+":""}{growth.toFixed(2)}%</span>}
          {data&&points.length>1&&<span title="Dihitung dari equity real-time (termasuk floating P&L & drawdown historis) — berbeda dari ROI di atas yang closed-only. Grafik ini menunjukkan volatilitas akun sebenarnya." style={{fontSize:11,color:"var(--text-faint)",cursor:"help",border:"1px solid var(--border)",borderRadius:"50%",width:14,height:14,display:"inline-flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>?</span>}
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <div style={{display:"flex",gap:10,marginRight:8}}>
            <div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:12,height:2,background:"var(--accent)",borderRadius:2}}/><span style={{fontSize:11,color:"var(--text-faint)"}}>Equity</span></div>
            <div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:12,height:2,background:"var(--text-faint)",borderRadius:2,opacity:0.5}}/><span style={{fontSize:11,color:"var(--text-faint)"}}>Balance</span></div>
          </div>
          <select value={days} onChange={e=>setDays(Number(e.target.value))} style={selStyle}>
            <option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option><option value={180}>180 days</option><option value={365}>1 year</option><option value={3650}>All Time</option>
          </select>
        </div>
      </div>
      {loading?<div style={{height:160,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text-faint)",fontSize:13}}>Loading...</div>:validPts.length<2?<div style={{height:160,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text-faint)",fontSize:13}}>No data yet</div>:(
        <div style={{position:"relative"}}>
          <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:"auto",display:"block"}}
            onMouseMove={e=>{const rect=svgRef.current?.getBoundingClientRect();if(!rect)return;const x=(e.clientX-rect.left)/rect.width*W;const idx=Math.min(validPts.length-1,Math.max(0,Math.round((x-PAD.left)/innerW*(validPts.length-1))));setHover({idx,x:xOf(idx),point:validPts[idx]});}}
            onMouseLeave={()=>setHover(null)}>
            <defs><linearGradient id="pub-eq-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--accent)" stopOpacity="0.25"/><stop offset="100%" stopColor="var(--accent)" stopOpacity="0.02"/></linearGradient></defs>
            {yTickVals.map((v,i)=>(<g key={i}><line x1={PAD.left} y1={yOf(v)} x2={W-PAD.right} y2={yOf(v)} stroke="var(--border-subtle)" strokeWidth="0.5" strokeDasharray="4 4"/><text x={PAD.left-6} y={yOf(v)+4} textAnchor="end" fontSize="10" fill="var(--text-faint)">{fmtMoney(v)}</text></g>))}
            <path d={toFill(balanceVals)} fill="rgba(148,163,184,0.06)"/>
            <path d={toFill(equityVals)} fill="url(#pub-eq-fill)"/>
            <path d={toPath(balanceVals)} fill="none" stroke="var(--text-faint)" strokeWidth="1" strokeDasharray="4 3" opacity="0.5"/>
            <path d={toPath(equityVals)} fill="none" stroke="var(--accent)" strokeWidth="2"/>
            {xTickIdxs.map(i=><text key={i} x={xOf(i)} y={H-6} textAnchor="middle" fontSize="10" fill="var(--text-faint)">{fmtDate(points[i]?.time)}</text>)}
            {hover&&(<><line x1={hover.x} y1={PAD.top} x2={hover.x} y2={H-PAD.bottom} stroke="var(--accent)" strokeWidth="0.8" opacity="0.5"/><circle cx={hover.x} cy={yOf(hover.point.equity)} r="4" fill="var(--accent)" stroke="var(--bg-surface)" strokeWidth="2"/><circle cx={hover.x} cy={yOf(hover.point.balance)} r="3" fill="var(--text-faint)" stroke="var(--bg-surface)" strokeWidth="2"/></>)}
          </svg>
          {hover&&hover.point&&(<div style={{position:"absolute",top:8,left:hover.idx>points.length*0.6?"auto":(hover.x/W*100)+"%",right:hover.idx>points.length*0.6?(100-hover.x/W*100)+"%":"auto",background:"var(--bg-elevated)",border:"1px solid var(--border)",borderRadius:"var(--radius-md)",padding:"8px 12px",fontSize:11,pointerEvents:"none",whiteSpace:"nowrap",zIndex:10}}>
            <div style={{color:"var(--text-faint)",marginBottom:4}}>{hover.point.time?new Date(hover.point.time).toLocaleDateString():""}</div>
            <div style={{display:"flex",gap:12}}>
              <div><div style={{color:"var(--text-faint)"}}>Equity</div><div style={{fontWeight:700,color:"var(--accent)"}}>{fmtMoney(hover.point.equity)}</div></div>
              <div><div style={{color:"var(--text-faint)"}}>Balance</div><div style={{fontWeight:600,color:"var(--text-main)"}}>{fmtMoney(hover.point.balance)}</div></div>
            </div>
          </div>)}
        </div>
      )}
      {data&&points.length>1&&(
        <div style={{display:"flex",gap:16,marginTop:12,paddingTop:12,borderTop:"1px solid var(--border-subtle)",flexWrap:"wrap",alignItems:"flex-end"}}>
          {[{label:"Start",value:fmtMoney(data.start)},{label:"Current",value:fmtMoney(data.end),color:growthColor},{label:"Peak",value:fmtMoney(data.max_equity)},{label:"Max DD",value:data.max_dd_pct>0?fmtPct(data.max_dd_pct):"—",color:data.max_dd_pct>0?"var(--danger)":"var(--text-faint)"}].map(s=>(
            <div key={s.label}><div style={{fontSize:10,color:"var(--text-faint)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>{s.label}</div><div style={{fontSize:13,fontWeight:700,color:s.color||"var(--text-main)"}}>{s.value}</div></div>
          ))}
          {(()=>{ const first=data.first_trade_date?new Date(data.first_trade_date):new Date(validPts[0].time); const last=new Date(validPts[validPts.length-1].time); const d=Math.round((last-first)/(1000*60*60*24)); const fmt=dt=>`${dt.getDate()}/${dt.getMonth()+1}/${dt.getFullYear()}`; return <div style={{marginLeft:"auto"}}><div style={{fontSize:10,color:"var(--text-faint)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>Trading Period</div><div style={{fontSize:11,fontWeight:600,color:"var(--text-muted)"}}>{fmt(first)} → {fmt(last)} <span style={{color:"var(--accent)"}}>({d} days)</span></div></div>; })()}
        </div>
      )}
    </div>
  );
}

function PublicDrawdownChart({ accountId }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [days, setDays] = React.useState(3650);
  const [hover, setHover] = React.useState(null);
  const svgRef = React.useRef(null);
  React.useEffect(() => {
    if (!accountId) return;
    setLoading(true);
    fetch(`/api/public/trader-equity-curve?account_id=${accountId}&days=${days}`)
      .then(r=>r.json()).then(d=>{ setData(d); setLoading(false); }).catch(()=>setLoading(false));
  }, [accountId, days]);
  const selStyle = {background:"var(--bg-elevated)",border:"1px solid var(--border)",color:"var(--text-main)",borderRadius:"var(--radius-md)",padding:"5px 10px",fontSize:12};
  const points = data?.points || [];
  const ddSeries = React.useMemo(() => {
    let peak=0;
    return points.map(p=>{ const hi=p.max_equity||p.equity||0; const lo=p.min_equity||p.equity||0; if(hi>peak) peak=hi; const dd=peak>0?((peak-lo)/peak)*100:0; return {time:p.time,dd:-dd,equity:p.equity}; });
  }, [points]);
  const W=900,H=160,PAD={top:12,right:16,bottom:28,left:52};
  const innerW=W-PAD.left-PAD.right, innerH=H-PAD.top-PAD.bottom;
  const minDD=ddSeries.length?Math.min(...ddSeries.map(d=>d.dd),-1)*1.1:-10;
  const xOf=i=>PAD.left+(i/Math.max(ddSeries.length-1,1))*innerW;
  const yOf=v=>PAD.top+(v/minDD)*innerH;
  const toPath=()=>{ if(!ddSeries.length)return""; return ddSeries.map((d,i)=>`${i===0?"M":"L"}${xOf(i).toFixed(1)},${yOf(d.dd).toFixed(1)}`).join(" "); };
  const toFill=()=>{ if(!ddSeries.length)return""; return toPath()+` L${xOf(ddSeries.length-1).toFixed(1)},${PAD.top} L${xOf(0).toFixed(1)},${PAD.top} Z`; };
  const xTickCount=Math.min(6,ddSeries.length);
  const xTickIdxs=ddSeries.length<=1?[0]:Array.from({length:xTickCount},(_,i)=>Math.round(i*(ddSeries.length-1)/(xTickCount-1)));
  const fmtDate=str=>{ if(!str)return""; const d=new Date(str); return `${d.getDate()}/${d.getMonth()+1}`; };
  const yTicks=[0,-5,-10,-20,-30].filter(v=>v>=minDD);
  const maxDD=data?.max_dd_pct??0;
  const ddColor=maxDD<=10?"var(--success)":maxDD<=20?"var(--warning)":"var(--danger)";
  return (
    <div style={{background:"var(--bg-surface)",borderRadius:"var(--radius-lg)",border:"1px solid var(--border)",padding:20,marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Drawdown — Underwater Curve</div>
          {data&&maxDD>=0&&<span style={{fontSize:12,fontWeight:700,padding:"2px 8px",borderRadius:"var(--radius-sm)",background:"var(--danger-soft)",color:ddColor}}>Max DD: {fmtPct(maxDD)}</span>}
        </div>
        <select value={days} onChange={e=>setDays(Number(e.target.value))} style={selStyle}>
          <option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option><option value={180}>180 days</option><option value={365}>1 year</option><option value={3650}>All Time</option>
        </select>
      </div>
      {loading?<div style={{height:120,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text-faint)",fontSize:13}}>Loading...</div>:ddSeries.length<2?<div style={{height:120,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text-faint)",fontSize:13}}>No data yet</div>:(
        <div style={{position:"relative"}}>
          <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:"auto",display:"block"}}
            onMouseMove={e=>{const rect=svgRef.current?.getBoundingClientRect();if(!rect)return;const x=(e.clientX-rect.left)/rect.width*W;const idx=Math.min(ddSeries.length-1,Math.max(0,Math.round((x-PAD.left)/innerW*(ddSeries.length-1))));setHover({idx,x:xOf(idx),point:ddSeries[idx]});}}
            onMouseLeave={()=>setHover(null)}>
            <defs><linearGradient id="pub-dd-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="rgba(239,68,68,0.35)"/><stop offset="100%" stopColor="rgba(239,68,68,0.05)"/></linearGradient></defs>
            {yTicks.map(v=>(<g key={v}><line x1={PAD.left} y1={yOf(v)} x2={W-PAD.right} y2={yOf(v)} stroke="var(--border-subtle)" strokeWidth="0.5" strokeDasharray="4 4"/><text x={PAD.left-4} y={yOf(v)+4} textAnchor="end" fontSize="10" fill="var(--text-faint)">{v}%</text></g>))}
            <line x1={PAD.left} y1={PAD.top} x2={W-PAD.right} y2={PAD.top} stroke="var(--border)" strokeWidth="1"/>
            <path d={toFill()} fill="url(#pub-dd-fill)"/>
            <path d={toPath()} fill="none" stroke="var(--danger)" strokeWidth="1.5"/>
            {xTickIdxs.map(i=><text key={i} x={xOf(i)} y={H-6} textAnchor="middle" fontSize="10" fill="var(--text-faint)">{fmtDate(ddSeries[i]?.time)}</text>)}
            {hover&&(<><line x1={hover.x} y1={PAD.top} x2={hover.x} y2={H-PAD.bottom} stroke="var(--danger)" strokeWidth="0.8" opacity="0.4"/><circle cx={hover.x} cy={yOf(hover.point.dd)} r="4" fill="var(--danger)" stroke="var(--bg-surface)" strokeWidth="2"/></>)}
          </svg>
          {hover&&hover.point&&(<div style={{position:"absolute",top:8,left:hover.idx>ddSeries.length*0.6?"auto":(hover.x/W*100)+"%",right:hover.idx>ddSeries.length*0.6?(100-hover.x/W*100)+"%":"auto",background:"var(--bg-elevated)",border:"1px solid var(--border)",borderRadius:"var(--radius-md)",padding:"8px 12px",fontSize:11,pointerEvents:"none",whiteSpace:"nowrap",zIndex:10}}>
            <div style={{color:"var(--text-faint)",marginBottom:4}}>{hover.point.time?new Date(hover.point.time).toLocaleDateString():""}</div>
            <div style={{fontWeight:700,color:"var(--danger)"}}>DD: {Math.abs(hover.point.dd).toFixed(2)}%</div>
          </div>)}
        </div>
      )}
    </div>
  );
}

function PerformanceTab({ accountId, colors:C, cardStyle, createdAt, deposit, detailStats }) {
  const [monthly, setMonthly] = React.useState([]);
  const [weekly, setWeekly] = React.useState([]);
  const [stats, setStats] = React.useState(null);
  const [heatmap, setHeatmap] = React.useState([]);
  const [heatPeriod, setHeatPeriod] = React.useState("3M");
  const [bias, setBias] = React.useState([]);
  const [heatView, setHeatView] = React.useState("pnl");
  React.useEffect(()=>{
    const token = localStorage.getItem("access_token")||localStorage.getItem("token")||"";
    const h = { Authorization:`Bearer ${token}` };
    fetch(`/api/investor/trader-monthly-performance?account_id=${accountId}`,{headers:h})
      .then(r=>r.json()).then(d=>{ if(d.months) setMonthly(d.months.slice(-12)); });
    fetch(`/api/investor/trader-weekly-performance?account_id=${accountId}`,{headers:h})
      .then(r=>r.json()).then(d=>{ if(d.weeks) setWeekly(d.weeks.slice(-16)); });
    // statistics dari detailStats prop (dari /api/public/trader/:id)
    if(detailStats) setStats(detailStats);
    fetch(`/api/public/trader-heatmap?account_id=${accountId}&days=3650`)
      .then(r=>r.json()).then(d=>{ if(d.ok) setHeatmap(d.heatmap||[]); });
    fetch(`/api/public/trader-bias?account_id=${accountId}`)
      .then(r=>r.json()).then(d=>{ if(d.ok) setBias(d.bias||[]); });
  },[accountId]);

  const maxM = Math.max(...monthly.map(m=>Math.abs(m.profit||m.net_profit||0)),1);
  const maxW = Math.max(...weekly.map(w=>Math.abs(w.profit||0)),1);

  return (
    <div>
      {/* Stats Grid */}
      {stats && (
        <div style={{...cardStyle, marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)",marginBottom:14}}>Performance Statistics</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:10}}>
            {[
              {l:"Total Trades", v:stats.totalTrades},
              {l:"Winning",      v:stats.winningTrades, c:"var(--success)"},
              {l:"Losing",       v:stats.losingTrades,  c:"var(--danger)"},
              {l:"Win Rate",     v:fmtPct(stats.winRate)},
              {l:"Profit Factor",v:fmt2(stats.profitFactor)},
              {l:"Avg Win",      v:fmtDollar(stats.avgWin), c:"var(--success)"},
              {l:"Avg Loss",     v:fmtDollar(stats.avgLoss), c:"var(--danger)"},
              {l:"Risk:Reward",  v:fmt2(stats.riskReward)},
              {l:"Expectancy",   v:fmtDollarSign(stats.expectancy),
                c:stats.expectancy>=0?"var(--success)":"var(--danger)"},
              {l:"Max DD",       v:fmtPct(stats.maxDrawdown),
                c:stats.maxDrawdown>20?"var(--danger)":stats.maxDrawdown>10?"var(--warning)":"var(--success)"},
            ].map(s=>(
              <div key={s.l} style={{background:"var(--bg-elevated)",borderRadius:8,padding:"10px 12px",border:"1px solid var(--border)"}}>
                <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:600,letterSpacing:"0.07em",textTransform:"uppercase",marginBottom:4}}>{s.l}</div>
                <div style={{fontSize:16,fontWeight:700,color:s.c||"var(--text-main)"}}>{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Equity Curve + DD Curve */}
      <PublicEquityChart accountId={accountId} />
      <PublicDrawdownChart accountId={accountId} />
      {heatmap.length > 0 && (()=>{
        const HOURS = Array.from({length:24},(_,i)=>i);
        const allHeatDates = [...new Set(heatmap.map(c=>c.date?.slice(0,10)||c.date))].sort();
        const dates = (()=>{
          const days={"1M":30,"3M":90,"6M":180,"1Y":365,"All":9999}[heatPeriod]||90;
          const cutoff = new Date(Date.now()-days*86400000).toISOString().slice(0,10);
          const filtered = allHeatDates.filter(d=>d>=cutoff);
          // Max 60 dates untuk performa — ambil terbaru
          return filtered.slice(-60);
        })();
        const cellMap = {};
        heatmap.forEach(c=>{ const d=c.date?.slice(0,10)||c.date; cellMap[`${d}-${c.hour}`]={...c,date:d}; });
        const DAY_NAMES = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
        const fmtDateLabel = d=>{ const dt=new Date(d); return `${dt.getDate()}/${dt.getMonth()+1}`; };
        const getValue = c => {
          if(!c) return 0;
          if(heatView==="pnl") return c.net_pnl;
          if(heatView==="trades") return c.trades;
          if(heatView==="winrate") return c.trades>0?c.wins/c.trades*100:0;
          return 0;
        };
        const allVals = heatmap.map(c=>getValue(c)).filter(v=>v!==0);
        const maxVal = allVals.length?Math.max(...allVals.map(Math.abs)):1;
        const getColor = c => {
          if(!c||c.trades===0) return "var(--bg-elevated)";
          const val=getValue(c);
          if(heatView==="trades") return `rgba(78,154,241,${0.15+Math.min(val/maxVal,1)*0.6})`;
          if(heatView==="winrate") return val>=60?`rgba(34,197,94,${0.3+(val-60)/40*0.5})`:val>=40?"rgba(245,158,11,0.3)":"rgba(239,68,68,0.4)";
          if(val>0) return `rgba(34,197,94,${0.15+Math.min(val/maxVal,1)*0.65})`;
          return `rgba(239,68,68,${0.15+Math.min(Math.abs(val)/maxVal,1)*0.65})`;
        };
        const fmtVal = c => {
          if(!c||c.trades===0) return "";
          if(heatView==="pnl"){ const v=c.net_pnl; if(Math.abs(v)<0.01) return ""; return (v>=0?"+":"")+(Math.abs(v)>=1000?(v/1000).toFixed(1)+"K":Math.abs(v)>=1?v.toFixed(0):v.toFixed(2)); }
          if(heatView==="trades") return c.trades;
          if(heatView==="winrate") return Math.round(c.wins/c.trades*100)+"%";
          return "";
        };
        // Bias from bias array
        const buyB = bias.find(b=>b.direction==="buy")||{count:0,net_profit:0,win_rate:0,pct:0};
        const sellB = bias.find(b=>b.direction==="sell")||{count:0,net_profit:0,win_rate:0,pct:0};
        const totalB = (buyB.count||0)+(sellB.count||0);
        const buyPct = totalB>0?(buyB.count/totalB*100):0;
        const sellPct = totalB>0?(sellB.count/totalB*100):0;
        const selSt = {background:"var(--bg-elevated)",border:"1px solid var(--border)",color:"var(--text-main)",borderRadius:6,padding:"4px 8px",fontSize:11};
        return (
          <div style={{...cardPad, marginBottom:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Trading Hours Heatmap</div>
              <div style={{display:"flex",gap:4}}>
                {["1M","3M","6M","1Y","All"].map(p=>(
                  <button key={p} onClick={()=>setHeatPeriod(p)} style={{padding:"3px 10px",fontSize:11,borderRadius:20,border:`1px solid ${heatPeriod===p?"var(--accent)":"var(--border)"}`,background:heatPeriod===p?"var(--accent)":"transparent",color:heatPeriod===p?"#fff":"var(--text-muted)",cursor:"pointer"}}>{p}</button>
                ))}
              </div>
              <select value={heatView} onChange={e=>setHeatView(e.target.value)} style={selSt}>
                <option value="pnl">Net P&L</option>
                <option value="trades">Trade Count</option>
                <option value="winrate">Win Rate</option>
              </select>
            </div>
            <div style={{overflowX:"auto"}}>
              <div style={{display:"grid",gridTemplateColumns:"36px repeat(24,1fr)",gap:2,minWidth:600}}>
                <div style={{fontSize:9,color:"var(--text-faint)",textAlign:"right",paddingRight:4}}>Date</div>
                {HOURS.map(h=><div key={h} style={{fontSize:9,color:"var(--text-faint)",textAlign:"center"}}>{h}</div>)}
                {dates.map(date=>{
                  const dt=new Date(date);
                  const dayName=DAY_NAMES[dt.getUTCDay()];
                  return (
                    <React.Fragment key={date}>
                      <div style={{fontSize:9,color:"var(--text-faint)",display:"flex",flexDirection:"column",alignItems:"flex-end",justifyContent:"center",paddingRight:4,lineHeight:1.2}}>
                        <span style={{fontWeight:600}}>{fmtDateLabel(date)}</span>
                        <span style={{fontSize:8,opacity:0.6}}>{dayName}</span>
                      </div>
                      {HOURS.map(h=>{
                        const c=cellMap[`${date}-${h}`];
                        return <div key={h} title={c?`${date} ${h}:00 UTC — ${c.trades||0} trades, P&L: $${(c.net_pnl||0).toFixed(2)}`:""}
                          style={{height:20,borderRadius:3,background:getColor(c),display:"flex",alignItems:"center",justifyContent:"center",
                            fontSize:8,fontWeight:600,color:c&&c.trades>0?"var(--text-main)":"transparent",
                            border:"1px solid rgba(255,255,255,0.04)"}}>
                          {fmtVal(c)}
                        </div>;
                      })}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
            <div style={{fontSize:10,color:"var(--text-faint)",marginTop:6,textAlign:"center"}}>Hour (UTC) — hover cell for details</div>
            {totalB>0 && (
              <div style={{marginTop:16,paddingTop:14,borderTop:"1px solid var(--border-subtle)"}}>
                <div style={{fontSize:12,fontWeight:600,color:"var(--text-main)",marginBottom:10}}>Long vs Short Bias</div>
                <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
                  <div style={{flex:1,minWidth:200}}>
                    <div style={{display:"flex",height:10,borderRadius:6,overflow:"hidden",marginBottom:6}}>
                      <div style={{width:`${buyPct}%`,background:"var(--success)",transition:"width 0.5s"}}/>
                      <div style={{width:`${sellPct}%`,background:"var(--danger)",transition:"width 0.5s"}}/>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between"}}>
                      <span style={{fontSize:11,color:"var(--success)",fontWeight:600}}>Long {buyPct.toFixed(1)}%</span>
                      <span style={{fontSize:11,color:"var(--danger)",fontWeight:600}}>Short {sellPct.toFixed(1)}%</span>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:16}}>
                    <div>
                      <div style={{fontSize:10,color:"var(--text-faint)",marginBottom:2}}>Buy Trades</div>
                      <div style={{fontSize:13,fontWeight:700,color:"var(--success)"}}>{buyB.count}</div>
                      <div style={{fontSize:10,color:"var(--text-faint)"}}>${(buyB.net_profit||0).toFixed(0)} P&L</div>
                    </div>
                    <div>
                      <div style={{fontSize:10,color:"var(--text-faint)",marginBottom:2}}>Sell Trades</div>
                      <div style={{fontSize:13,fontWeight:700,color:"var(--danger)"}}>{sellB.count}</div>
                      <div style={{fontSize:10,color:"var(--text-faint)"}}>${(sellB.net_profit||0).toFixed(0)} P&L</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}
      {/* Monthly Chart */}
      <div style={{...cardStyle, marginBottom:16}}>
        <MonthlyChart accountId={accountId} accountCreatedAt={createdAt} deposit={deposit||0} />
      </div>
      {/* Weekly Chart */}
      <div style={{...cardStyle, marginBottom:16}}>
        <WeeklyChart accountId={accountId} accountCreatedAt={createdAt} deposit={deposit||0} />
      </div>
    </div>
  );
}
// Wed Jun 24 15:19:51 WIB 2026
